import { marked } from 'marked'
import DOMPurify from 'dompurify'

/**
 * Parses a JSON object from a string, even if it's embedded in other text.
 * @param responseText The string that might contain a JSON object.
 * @returns A parsed JSON object or null if not found or invalid.
 */
const parseJsonFromText = (responseText: string): object | null => {
  if (!responseText || typeof responseText !== 'string') return null

  // This regex matches a JSON object, including nested objects and arrays.
  // It finds the first top-level curly-brace block.
  const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/
  const match = responseText.match(jsonBlockRegex)

  if (!match) return null

  try {
    const parsed = JSON.parse(match[1])
    // Ensure it's a non-empty object or array
    if (parsed && typeof parsed === 'object') {
      console.log('Parsed JSON:', parsed)
      return parsed
    }
    return null
  } catch (e) {
    console.error('failed to parse JSON from code block: ', e)
    return null
  }
}

/**
 * Formats raw agent text into sanitized HTML and extracts an embedded JSON object.
 * @param text The raw text from the agent.
 * @returns An object containing the formatted HTML and the parsed JSON.
 */
export const useChatFormatter = async (text: string) => {
  if (!text) return { formattedText: '', json: null }

  // First, try to parse JSON from the raw text
  const json = parseJsonFromText(text)

  // Remove any "thinking" blocks before converting to HTML
  let cleanText = text.replace(/<thinking>[\s\S]*?<\/thinking>/g, '')

  // Escape HTML characters to prevent them from being interpreted as tags by marked
  cleanText = cleanText.replace(/</g, '&lt;').replace(/>/g, '&gt;')

  // apply line breaks \n to the text
  const withLineBreaks = cleanText.replace(/\n/g, '<br />')

  // Convert Markdown to HTML and sanitize it to prevent XSS attacks
  const rawHtml = await marked(withLineBreaks, { gfm: true })
  const formattedText = DOMPurify.sanitize(rawHtml)

  return { formattedText, json }
}
